import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { startGrade } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { ArrowRight, Sparkles, Search, TrendingUp, CheckCircle2, AlertCircle } from "lucide-react";
import PropertyAutocomplete from "@/components/PropertyAutocomplete";

export default function Landing() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    city: "",
    state: "",
    country: "United States",
    website: "",
  });
  // Track which fields were auto-filled from Google (so we can show a subtle
  // confirmation indicator). Cleared on manual edit of each field.
  const [autofilled, setAutofilled] = useState({
    name: false,
    city: false,
    state: false,
    country: false,
    website: false,
  });
  const [placeId, setPlaceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    // Any manual edit clears the "from Google" flag on that field.
    setAutofilled((a) => (a[k] ? { ...a, [k]: false } : a));
  };

  const handlePlacePick = (details) => {
    setForm((f) => ({
      ...f,
      name: details.name || f.name,
      city: details.city || f.city,
      state: details.state || f.state,
      country: details.country || f.country,
      website: details.website || f.website,
    }));
    setAutofilled({
      name: !!details.name,
      city: !!details.city,
      state: !!details.state,
      country: !!details.country,
      website: !!details.website,
    });
    setPlaceId(details.place_id || null);
  };

  const canSubmit = form.name.trim() && form.city.trim() && form.country.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await startGrade(form);
      nav(`/grading/${res.analysis_id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid={TESTIDS.landingHero}>
        <div className="grain absolute inset-0" />
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(107,122,85,0.22) 0%, transparent 60%)",
            filter: "blur(80px)",
          }}
        />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 fade-in">
              <div className="label-eyebrow mb-4 flex items-center gap-2">
                <Sparkles className="w-3 h-3" style={{ color: "var(--brand-sage)" }} />
                FieldGuide · The Local Authority Platform for Hotels
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight" style={{ color: "var(--text-primary)" }}>
                When travelers ask AI about your hotel&apos;s
                <span className="italic" style={{ color: "var(--brand-sage)" }}> local area</span>, are you part of the conversation?
              </h1>
              <p className="mt-6 text-lg md:text-xl text-[color:var(--text-secondary)] max-w-2xl leading-relaxed">
                In 1&ndash;2 minutes, you&apos;ll see how often your hotel appears, who is
                answering queries your property should own, and where your strongest opportunities
                for improvement lie.
              </p>
              <div className="flex items-center gap-6 mt-8 text-sm" style={{ color: "var(--text-secondary)" }}>
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
                  24 travel-planning queries
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
                  3 AI platforms tested
                </div>
              </div>
            </div>

            {/* Form card */}
            <div className="lg:col-span-5 fade-in" style={{ animationDelay: "120ms" }}>
              <form
                onSubmit={onSubmit}
                className="glass rounded-2xl p-6 md:p-8 relative"
                style={{ boxShadow: "0 40px 80px -30px rgba(0,0,0,0.6)" }}
              >
                <div className="mb-6">
                  <div className="label-eyebrow mb-1">Free Local Authority analysis</div>
                  <div className="font-serif text-2xl">Which hotel should we grade?</div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="label-eyebrow block mb-1.5">Property name</label>
                    <PropertyAutocomplete
                      value={form.name}
                      onChange={setField("name")}
                      onPick={handlePlacePick}
                      testId={TESTIDS.landingPropertyName}
                    />
                    {autofilled.name && (
                      <p
                        className="mt-1.5 text-xs flex items-center gap-1.5"
                        style={{ color: "var(--brand-sage-deep)" }}
                        data-testid="landing-autofill-name"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Property matched via Google — city, state, and website filled in below.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className="label-eyebrow block mb-1.5 flex items-center gap-1.5">
                        City
                        {autofilled.city && (
                          <CheckCircle2
                            className="w-3 h-3"
                            style={{ color: "var(--brand-sage-deep)" }}
                            aria-label="Filled from Google"
                          />
                        )}
                      </label>
                      <input
                        className="input-base"
                        placeholder="Princeton"
                        value={form.city}
                        onChange={setField("city")}
                        data-testid={TESTIDS.landingCity}
                        required
                      />
                    </div>
                    <div>
                      <label className="label-eyebrow block mb-1.5 flex items-center gap-1.5">
                        State
                        {autofilled.state && (
                          <CheckCircle2
                            className="w-3 h-3"
                            style={{ color: "var(--brand-sage-deep)" }}
                            aria-label="Filled from Google"
                          />
                        )}
                      </label>
                      <input
                        className="input-base"
                        placeholder="NJ"
                        value={form.state}
                        onChange={setField("state")}
                        data-testid={TESTIDS.landingState}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1.5 flex items-center gap-1.5">
                      Country
                      {autofilled.country && (
                        <CheckCircle2
                          className="w-3 h-3"
                          style={{ color: "var(--brand-sage-deep)" }}
                          aria-label="Filled from Google"
                        />
                      )}
                    </label>
                    <input
                      className="input-base"
                      placeholder="United States"
                      value={form.country}
                      onChange={setField("country")}
                      data-testid={TESTIDS.landingCountry}
                      required
                    />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1.5">
                      Official hotel website
                    </label>
                    <input
                      className="input-base"
                      placeholder="https://yourhotel.com"
                      value={form.website}
                      onChange={setField("website")}
                      data-testid={TESTIDS.landingWebsite}
                    />
                    {autofilled.website ? (
                      <div
                        className="mt-2 flex items-start gap-2 rounded-lg px-3 py-2"
                        style={{
                          background: "rgba(166,124,63,0.08)",
                          border: "1px solid rgba(166,124,63,0.28)",
                        }}
                        data-testid="landing-website-verify"
                      >
                        <AlertCircle
                          className="w-4 h-4 mt-0.5 flex-shrink-0"
                          style={{ color: "var(--brand-gold-deep, #8a6a2f)" }}
                        />
                        <div className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                          <span className="font-medium" style={{ color: "var(--text-primary)" }}>
                            Please verify website carefully.
                          </span>{" "}
                          We use this to establish content ownership (a big part of your score),
                          and to build suggested content compatible with your property&apos;s brand.
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
                        Recommended so we can identify and credit your owned content accurately.
                      </p>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="mt-4 text-sm text-[color:var(--score-low)]" data-testid="landing-error">
                    {error}
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                  <a
                    href="/sample-report"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs underline underline-offset-4 transition-colors"
                    style={{ color: "var(--text-muted)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand-sage)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
                    data-testid={TESTIDS.landingSample}
                  >
                    View a sample report
                  </a>
                  <button
                    type="submit"
                    disabled={!canSubmit || loading}
                    className="btn-gold"
                    data-testid={TESTIDS.landingSubmit}
                  >
                    {loading ? "Starting..." : "See My Local Authority Score"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="mt-5 text-xs text-[color:var(--text-muted)] leading-relaxed">
                  Takes about 1&ndash;2 minutes. See your score before entering any contact information.
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              step: "01",
              title: "We generate 24 real-world queries",
              body: "Claude Sonnet 4.5 studies your property and destination, then generates the travel-planning questions real guests actually ask AI — from “where to stay when visiting Princeton University” to “best romantic dinner in your city.”",
            },
            {
              step: "02",
              title: "We ask ChatGPT, Google AI, and Perplexity",
              body: "Each query is sent to the three AI platforms most travelers use. We record whether your hotel appears, whether your website is cited, and who else is dominating the answer.",
            },
            {
              step: "03",
              title: "You get a defensible score",
              body: "Every attribute is scored against stored evidence. You see your headline FieldGuide Score, four sub-scores, and five prioritized content moves to close specific gaps.",
            },
          ].map((s) => (
            <div key={s.step} className="glass glass-hover rounded-2xl p-6 md:p-8">
              <div className="mb-3" style={{ fontFamily: "Playfair Display", fontStyle: "italic", fontSize: "0.95rem", color: "var(--brand-sage)" }}>{s.step}</div>
              <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
              <p className="text-[color:var(--text-secondary)] leading-relaxed text-sm md:text-base">
                {s.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 glass rounded-2xl p-8 md:p-12 relative overflow-hidden">
          <div
            className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full opacity-30 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(107,122,85,0.14) 0%, transparent 60%)",
              filter: "blur(60px)",
            }}
          />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="label-eyebrow mb-3">The FieldGuide Local Authority Score<sup style={{ fontSize: "0.6em", marginLeft: "1px", letterSpacing: "0" }}>™</sup></div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
                A single number for how much authority your hotel has in AI-driven local discovery.
              </h2>
              <p className="text-[color:var(--text-secondary)] leading-relaxed">
                We weight what earns authority: your hotel being cited by name (20 pts), your
                official website showing up (25 pts), and — most heavily — your own content being
                the substantive answer (35 pts). Authority is owned, not intermediated.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[
                ["80–100", "Strong Local Authority", "score-strong"],
                ["65–79", "Visible, With Meaningful Gaps", "score-mid"],
                ["45–64", "Inconsistent or Intermediary-Dependent", "score-mid"],
                ["25–44", "Emerging Local Authority", "score-low"],
                ["0–24", "Largely Absent From Local Discovery", "score-low"],
              ].map(([r, l, cls]) => (
                <div
                  key={r}
                  className="glass rounded-xl p-4 flex flex-col gap-1"
                  data-testid={`band-${r}`}
                >
                  <div className={`font-serif text-lg font-medium ${cls}`}>{r}</div>
                  <div className="text-[color:var(--text-secondary)] text-[12px] leading-snug">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
